import React, {useCallback} from 'react';
import qs from 'query-string';
import { faPlus, faMinus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {CellProps, Column} from 'react-table';
import {ListTable} from 'components/pages/common/table/ListTable';
import {useForm} from 'react-hook-form';
import {useAsync} from "react-async-hook";
import {useLocation} from "react-router-dom";

import {roomApi} from '../../apis/admin/room/roomApi';
import useAppRouter from "../../hooks/useAppRouter";
import useLoading from '../../hooks/useLoading';
import {useAuth} from '../../hooks/useAuth';
import {SalesmanType} from "../../enum/user/salesmanType";
import {config} from "../../libs/config/config";
import {formatOmittedDateTime} from "../../libs/utils/formatDate";
import {logError} from "../../libs/utils/logError";


interface Room {
  roomId: number;
  roomName: string;
  content?: string;
  salesName: string;
  ownerName: string;
  ownerCompanyName: string;
  createDateTime?: string;
  taskUpdateDateTime?: string;
  status?: string;
  vhDefSetId?: string;
  vhSpaceSetId?: string;
  enabledFlg: boolean;
}

interface DataType extends Room {
  operation?: string
}

interface IndexRoomParams {
  companyName: string;
  salesName: string;
  ownerName: string;
  roomName: string;
  size: number;
  currentPage: number;
  sort: Array<string>;
}

export const RoomListPage: React.FC = () => {
  const { goto } = useAppRouter();
  const withLoading = useLoading();

  const location = useLocation();
  const { state: { currentUser } } = useAuth();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
  } = useForm({
    reValidateMode: 'onSubmit',
  });

  const [adminRoomList, setAdminRoomList] = React.useState<DataType[]>([]);
  const [salesRoomList, setSalesRoomList] = React.useState<DataType[]>([]);
  const [isShowSearchCondition, setIsShowSearchCondition] = React.useState<boolean>(false);
  const [abortController, setAbortController] = React.useState<AbortController | undefined>();

  const [totalCount, setTotalCount] = React.useState<number>(10);
  const [params, setParams] = React.useState<IndexRoomParams>({
    companyName: '',
    salesName: '',
    ownerName: '',
    roomName: '',
    size: config.list.pageSizeOptions[0],
    currentPage: 1,
    sort: [
      currentUser?.adminFlg === SalesmanType.ADMIN
        ? 'createDateTime:desc'
        : 'taskUpdateDateTime:desc'
    ],
  });

  useAsync(async () => {
    const fetch = async () => {
      if (abortController !== undefined) {
        abortController.abort();
        setAbortController(undefined);
      }

      setAbortController(new AbortController());

      withLoading(async () => {
        const offset = (params.currentPage - 1) * params.size;

        try {
          setValue('companyName', params.companyName);
          setValue('salesName', params.salesName);
          setValue('ownerName', params.ownerName);
          setValue('roomName', params.roomName);

          // Admin権限の場合
          if (currentUser?.adminFlg === SalesmanType.ADMIN) {
            const response = await roomApi.searchForAdmin(
              params.companyName,
              params.salesName,
              params.ownerName,
              params.roomName,
              params.size,
              offset,
              params.sort,
              {
                abortSignal: abortController?.signal,
              }
            );

            setAdminRoomList(response.rooms);
            setTotalCount(response.totalHits);
          }

          // 営業権限の場合
          if (currentUser?.adminFlg === SalesmanType.GENERAL) {
            const response = await roomApi.searchForSales(
              params.companyName,
              params.salesName,
              params.ownerName,
              params.roomName,
              params.size,
              offset,
              params.sort,
              {
                abortSignal: abortController?.signal,
              }
            );

            setSalesRoomList(response.rooms);
            setTotalCount(response.totalHits);
          }
          
          setAbortController(undefined);

        } catch(e) {
          await logError(
            location.pathname,
            'ROOM一覧の読み込みに失敗しました。',
            e
          );

          goto('internalServerError');
        }
      });
    };
    fetch();
  }, [params]);


  const handleSort = async (newSort: string[]) => {
    if ((params.sort.length === newSort.length)
      && params.sort.every((sort, index) => sort === newSort[index])) {
      return
    }

    setParams(prevState => ({
      ...prevState,
      sort: newSort,
      currentPage: 1,
    }));
  }

  const handlePageChange = async (selectedItem: { selected: number }) => {
    setParams(prevState => ({
      ...prevState,
      currentPage: selectedItem.selected + 1,
    }));
  }

  const handlePageSizeChange = async (newSize: number) => {
    setParams(prevState => ({
      ...prevState,
      size: newSize,
      currentPage: 1,
    }));
  }

  const handleCreate = async () => {
    goto('roomCreate');
  }

  const handleOpenTasklist = useCallback(async (id: number) => {
    withLoading(async () => {
      window.open(`/tasklist/${id}`, '_blank', 'noopener,noreferrer')
    });
  }, [withLoading]);

  const handleOpenRoom = useCallback(async (id: number) => {
    withLoading(async () => {
      if (config.room.page.release.url === undefined) {
        await logError(
          location.pathname,
          'ROOMのURLが設定されていません。',
        );
        goto('internalServerError');
        return;
      }
      window.open(`${config.room.page.release.url}?roomId=${id}`, '_blank', 'noopener,noreferrer');
    });
  }, [goto, location.pathname, withLoading]);

  const handleVhDefinePreviewClick = (vhSpaceSetId: string, vhDefinesId: string) => {
    const previewUrl = config.room.page.preview.url;
    const queryParams = {
      mainasset: config.room.file.threeDModel.url?.replace('<id>', vhSpaceSetId!),
      chat: config.room.file.balloonChatScenario.url?.replace('<id>', vhDefinesId!),
    }

    window.open(`${previewUrl}?${qs.stringify(queryParams)}`, '_blank');
  }

  const handleSearchFormSubmit = async (data: any) => {
    setParams(prevState => ({
      ...prevState,
      companyName: data.companyName,
      salesName: data.salesName,
      ownerName: data.ownerName,
      roomName: data.roomName,
      currentPage: 1,
    }));
  }

  const AdminOperationCell = React.useCallback(({ row }: CellProps<Room>) => (
    <>
    <div className="grid gap-1">
      <button className="btn-primary"
              type='button'
              onClick={() => {
                goto('roomEdit', undefined, { id: row.original.roomId });
              }}
      >
        編 集
      </button>
      <button className="btn-primary"
              type='button'
              onClick={() => {
                goto('roomMemberEdit', undefined, { id: row.original.roomId });
              }}
      >
        参加者編集
      </button>
      <button className="btn-primary text-center"
              type='button'
              onClick={() => handleVhDefinePreviewClick(row.original.vhSpaceSetId!, row.original.vhDefSetId!)}
      >
        プレビュー
      </button>
    </div>
    </>
  ), [goto]);

  const DateTimeCell = useCallback(({ value }: CellProps<DataType>) => (
    <div>{ value ? formatOmittedDateTime(value) : '' }</div>
  ), []);

  const SalesOperationCell = React.useCallback(({ row }: CellProps<Room>) => (
    <>
    <div className="grid gap-1">
      <button className="btn-primary"
              type='button'
              onClick={() => {
                goto('roomEdit', undefined, { id: row.original.roomId });
              }}
      >
        編 集
      </button>
      <button className="btn-primary"
              type='button'
              onClick={() => {
                goto('roomMemberEdit', undefined, { id: row.original.roomId });
              }}
      >
        参加者編集
      </button>
      {row.original.enabledFlg && (
        <button className="btn-primary text-center"
                type='button'
                onClick={() => handleOpenTasklist(row.original.roomId)}
        >
          タスクボード
        </button>
      )}
      {row.original.enabledFlg && (
        <button className="btn-primary focus:opacity-50 text-center"
                type='button'
                onClick={() => handleOpenRoom(row.original.roomId)}
        >
          ROOM起動
        </button>
      )}
    </div>
    </>
  ), [goto, handleOpenTasklist, handleOpenRoom]);

  const OwnerNameCell = React.useCallback(({ row }: CellProps<Room>) => (
    <>
      { row.original.ownerCompanyName } { row.original.ownerName }
    </>
  ), [])

  const adminColumns: Column<DataType>[] = React.useMemo(
    () => [
      {
        Header: '名称',
        accessor: 'roomName',
        className: 'select-none',
      },
      {
        Header: '説明',
        accessor: 'content',
        disableSortBy: true,
      },
      {
        Header: '主営業',
        accessor: 'salesName',
        disableSortBy: true,
      },
      {
        Header: 'オーナー',
        accessor: 'ownerName',
        Cell: OwnerNameCell,
        disableSortBy: true,
      },
      {
        Header: '作成日時',
        accessor: 'createDateTime',
        Cell: DateTimeCell,
      },
      {
        Header: '操作',
        accessor: 'operation',
        Cell: AdminOperationCell,
        disableSortBy: true,
      },
    ],
    [AdminOperationCell, OwnerNameCell, DateTimeCell]
  );

  const salesColumns: Column<DataType>[] = React.useMemo(
    () => [
      {
        Header: '名称',
        accessor: 'roomName',
        className: 'select-none',
      },
      {
        Header: '説明',
        accessor: 'content',
        disableSortBy: true,
      },
      {
        Header: '主営業',
        accessor: 'salesName',
        disableSortBy: true,
      },
      {
        Header: 'オーナー',
        accessor: 'ownerName',
        Cell: OwnerNameCell,
        disableSortBy: true,
      },
      {
        Header: 'タスクボード更新日時',
        accessor: 'taskUpdateDateTime',
        Cell: DateTimeCell,
      },
      {
        Header: '操作',
        accessor: 'operation',
        Cell: SalesOperationCell,
        disableSortBy: true,
      },
    ],
    [SalesOperationCell, OwnerNameCell, DateTimeCell,]
  );

  return (
    <>
      <div className="bg-sub min-h-full">
        <div className="mx-8 flex items-center py-6">
          <div className="text-xl text-primary font-semibold">ROOM一覧</div>
        </div>

        <div className="mx-8">
          <form onSubmit={handleSubmit(handleSearchFormSubmit)}>
            <div className="shadow-md border border-white px-6">
              <button
                type="button"
                className="text-primary focus:outline-none w-full"
                onClick={() => setIsShowSearchCondition(prevState => !prevState)}
              >
                <div className="py-4 flex justify-between">
                  <div className="text-primary font-bold">
                      検索条件
                  </div>
                  <div>
                    {isShowSearchCondition ? <FontAwesomeIcon icon={faMinus} /> : <FontAwesomeIcon icon={faPlus}  />}
                  </div>
                </div>
              </button>
              {isShowSearchCondition && (
                <div>
                  <div className="grid grid-cols-2 items-center gap-10">
                    { /* ROOM名 */ }
                    <div className="mb-4">
                      <label htmlFor="roomName">
                        <div className="text-sm font-semibold text-primary">
                          ROOM名
                        </div>
                        <input
                          /* eslint-disable-next-line react/jsx-props-no-spreading */
                          {...register('roomName', {})}
                          className="text-field"
                          id="roomName"
                          type="text"
                        />
                      </label>
                    </div>

                    { /* 企業名 */ }
                    <div className="mb-4">
                      <label htmlFor="companyName">
                        <div className="text-sm font-semibold text-primary">
                          企業名
                        </div>
                        <input
                          /* eslint-disable-next-line react/jsx-props-no-spreading */
                          {...register('companyName', {})}
                          className="text-field"
                          id="companyName"
                          type="text"
                        />
                      </label>
                    </div>

                  </div>

                  <div className="grid grid-cols-2 items-center gap-10">

                    { /* 営業名 */ }
                    <div className="mb-4">
                      <label htmlFor="salesName">
                        <div className="text-sm font-semibold text-primary">
                          営業名
                        </div>
                        <input
                          /* eslint-disable-next-line react/jsx-props-no-spreading */
                          {...register('salesName', {})}
                          className="text-field"
                          id="salesName"
                          type="text"
                        />
                      </label>
                    </div>

                    { /* オーナー名 */ }
                    <div className="mb-4">
                      <label htmlFor="ownerName">
                        <div className="text-sm font-semibold text-primary">
                          オーナー名
                        </div>
                        <input
                          /* eslint-disable-next-line react/jsx-props-no-spreading */
                          {...register('ownerName', {})}
                          className="text-field"
                          id="ownerName"
                          type="text"
                        />
                      </label>
                    </div>

                  </div>

                  <div className="p-4 border-gray-200 text-center">
                    <button type="button"
                            className="btn-sub mr-6"
                            onClick={() => {
                              reset();
                            }}
                    >
                      クリア
                    </button>
                    <button type="submit"
                            className="btn-primary"
                    >
                      検 索
                    </button>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>


        <div className="m-8 text-right">
          {currentUser?.adminFlg === SalesmanType.ADMIN && (
          <button type="button"
                  className="btn-primary"
                  onClick={() => {
                    handleCreate();
                  }}
          >
            追 加
          </button>
          )}
        </div>



        <div className="mx-8">

          {currentUser?.adminFlg === SalesmanType.ADMIN && (
            <>
              <ListTable
                tableData={adminRoomList}
                columns={adminColumns}
                onSort={handleSort}
                initialSort={params.sort}
                totalCount={totalCount}
                pageSize={params.size}
                currentPage={params.currentPage}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </>
          )}

          {currentUser?.adminFlg === SalesmanType.GENERAL && (
            <>
              <ListTable
                tableData={salesRoomList}
                columns={salesColumns}
                onSort={handleSort}
                initialSort={params.sort}
                totalCount={totalCount}
                pageSize={params.size}
                currentPage={params.currentPage}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};
